import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import useDebounce from '../../hooks/useDebounce'

interface AutoSuggestionProps {
  options: { label?: string; subLabel: string }[]
  onSelect: (value: { label?: string; subLabel: string } | null) => void
  clearInput: boolean
  displaySubLabel?: boolean
  placeholder?: string
  startAdornment?: React.ReactNode
  onInputChange: (value: string) => void
  isLoading: boolean
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  onClear: () => void
  handleScroll?: React.UIEventHandler<HTMLUListElement>
  style?: React.CSSProperties
}
const AutoSuggestion: FC<AutoSuggestionProps> = ({
  options,
  onSelect,
  displaySubLabel,
  placeholder,
  startAdornment,
  onInputChange,
  isLoading,
  clearInput,
  onKeyDown,
  onClear,
  handleScroll,
  style,
}) => {
  const [inputValue, setInputValue] = useState<string>('')
  const debouncedValue = useDebounce<string>(inputValue, 500)

  useEffect(() => {
    if (debouncedValue && debouncedValue.length >= 3) {
      onInputChange(debouncedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  const getInitials = (label?: string, subLabel?: string) => {
    if (label) {
      // Split the name into words
      const words = label.trim().split(/\s+/)
      // Handle case where there's at least a first and last name
      if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      }
      // If there's only one word, use its first two letters
      return words[0][0].toUpperCase() + (words[0][1]?.toLowerCase() || '')
    }
    if (subLabel) {
      const username = subLabel.split('@')[0] // Extract the username part
      return username[0].toUpperCase() + username[1].toLowerCase()
    }
  }

  const getAvatarStyle = (index: number) => {
    const colorCombinations = [
      { color: 'primary.main', backgroundColor: 'profileIcon.colorNine' },
      { color: 'primary.main', backgroundColor: 'profileIcon.colorTen' },
      { color: 'profileIcon.colorEight', backgroundColor: 'profileIcon.colorEleven' },
      { color: 'profileIcon.colorEight', backgroundColor: 'profileIcon.colorTwelve' },
    ]
    return colorCombinations[index % colorCombinations.length]
  }

  useEffect(() => {
    if (clearInput) {
      setInputValue('') // Clear the state value when clearInput is triggered
      onClear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearInput])

  return (
    <Autocomplete
      options={inputValue.length > 2 ? options : []}
      getOptionLabel={(option) => option.subLabel}
      loading={isLoading}
      value={null} // Reset the selected value
      inputValue={inputValue} // Control the input value
      onInputChange={(event, newValue) => setInputValue(newValue)}
      onChange={(event, newValue) => {
        onSelect(newValue)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={placeholder}
          onKeyDown={onKeyDown}
          InputProps={{
            ...params.InputProps,
            startAdornment: startAdornment,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color='inherit' size={24} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll, // Attach the scroll handler
        style,
      }}
      renderOption={(props, option, { index }) => (
        <li {...props}>
          <Box display='flex' sx={{ alignItems: 'center' }}>
            <Avatar
              sx={{
                marginRight: 1,
                fontFamily: 'Lexend',
                fontSize: '13px',
                fontWeight: '600',
                lineHeight: '20px',
                ...getAvatarStyle(index),
              }}
            >
              {getInitials(option.label, option.subLabel)}
            </Avatar>
            <Box display='flex' sx={{ flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: '400', lineHeight: '1.4' }}>
                {option.label}
              </Typography>
              {option.subLabel && displaySubLabel && (
                <Typography
                  variant='subtitle2'
                  sx={{ color: 'card.contrastText', lineHeight: '1.4' }}
                >
                  {option.subLabel}
                </Typography>
              )}
            </Box>
          </Box>
        </li>
      )}
    />
  )
}
export default AutoSuggestion
